/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  portfolioTrackerReportDataWithHistoryByInitiativeId,
  portfolioTrackerReportDataWithHistoryByInitiativeIdBySurveyId,
  reportDataWithHistoryByInitiativeId,
  reportDataWithHistoryByInitiativeIdBySurveyId
} from '../components/report-output/reportApi';
import { RootState } from '../reducers';
import { HistoricalReportData, HistoricalReportDataByPeriod } from '../types/reportData';
import { DataPeriods } from '../types/universalTracker';

interface SliceState {
  data: HistoricalReportData[];
  cachedPeriod?: DataPeriods;
  initiativeId?: string;
  type?: string;
  cachedByPeriod?: HistoricalReportDataByPeriod;
  surveyId?: string;
  errorMessage: string;
  errored: boolean;
  loaded: boolean;
  loading: boolean;
}
const createState = (): SliceState => ({
  data: [],
  errorMessage: '',
  errored: false,
  loaded: false,
  loading: false,
})

const insightsSlice = createSlice({
  name: 'insightsData',
  initialState: createState(),
  reducers: {
    loading: (state, action: PayloadAction<string>) => ({ ...state, initiativeId: action.payload, loading: true }),
    loadedByAvailPeriod(state, action: PayloadAction<{ period?: DataPeriods, data?: HistoricalReportDataByPeriod }>) {
      const dataByPeriod = action.payload.data || state.cachedByPeriod || {}
      const bestPeriod = [DataPeriods.Yearly, DataPeriods.Quarterly, DataPeriods.Monthly].find(p => dataByPeriod[p]);
      const period = action.payload.period ?? bestPeriod;
      const data = period ? (dataByPeriod[period] ?? []) : [];
      return { ...state, surveyId: undefined, cachedPeriod: period, data: data, cachedByPeriod: action.payload.data ?? state.cachedByPeriod, loaded: true, loading: false };
    },
    loadedBySurveyId(state, action: PayloadAction<{ surveyId: string, data?: HistoricalReportData }>) {
      const data = action.payload.data ? [action.payload.data] : [];
      return { ...state, surveyId: action.payload.surveyId, cachedPeriod: undefined, data: data, cachedByPeriod: undefined, loaded: true, loading: false };
    },
    handleError(state, action: PayloadAction<string>) {
      return {
        ...state,
        loaded: true,
        loading: false,
        errored: true,
        errorMessage: action.payload
      };
    },
  }
});


const { loading, handleError, loadedByAvailPeriod, loadedBySurveyId } = insightsSlice.actions

export const loadInsightsByPeriod = (initiativeId: string, type: 'ctl' | 'sgx_metrics', period?: DataPeriods, portfolioId?: string) =>
  (dispatch: any, getState: () => RootState) => {

    const sliceState: SliceState = getState().insightsData;

    // load from cached value
    if (sliceState.cachedByPeriod && sliceState.initiativeId === initiativeId && sliceState.type === type) {
      dispatch(loadedByAvailPeriod({ period }));
      return;
    }

    dispatch(loading(initiativeId));

    // load api call
    (portfolioId ?
      portfolioTrackerReportDataWithHistoryByInitiativeId(portfolioId, initiativeId, type)
      :
      reportDataWithHistoryByInitiativeId(initiativeId, type)
    )
      .then(data => {
        dispatch(loadedByAvailPeriod({ period, data: data ?? [] }))
      })
      .catch((e) => dispatch(handleError(e.message)));
  }

export const loadInsightsBySurveyId = (initiativeId: string, type: 'insights' | 'ctl' | 'sgx_metrics', surveyId: string, portfolioId?: string) =>
  (dispatch: any, getState: () => RootState) => {

    const sliceState: SliceState = getState().insightsData;

    // load from cached value
    if (sliceState.surveyId === surveyId) {
      return;
    }

    dispatch(loading(initiativeId));

    // load api call
    (portfolioId ?
      portfolioTrackerReportDataWithHistoryByInitiativeIdBySurveyId(portfolioId, initiativeId, type, surveyId)
      :
      reportDataWithHistoryByInitiativeIdBySurveyId(initiativeId, type, surveyId)
    )
      .then(data => {
        dispatch(loadedBySurveyId({ surveyId, data: data ?? [] }))
      })
      .catch((e) => dispatch(handleError(e.message)));
  }

export default insightsSlice.reducer
